"use client"

import { useState, useEffect, useRef, Suspense } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { motion } from "motion/react"
import { OverseedingForm, type OverseedingInitialValues } from "@/components/tools/overseeding-form"
import {
  HeadlineHero,
  RelatedTools,
  ResultEmailCaptureCard,
  ToolPageLoadingFallback,
} from "@/components/tools/shared"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { type OverseedGrassId, type OverseedingResult } from "@/lib/overseeding-calculator"
import { analytics, stashEmailCaptureAttribution } from "@/lib/analytics"
import { buildToolUrl } from "@/lib/result-tool-urls"
import { useCalculatorStarted } from "@/hooks/useCalculatorStarted"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { Reveal } from "@/components/motion"

const GRASS_PARAM: Record<string, OverseedGrassId> = {
  fescue: "fescue",
  tallfescue: "fescue",
  kbg: "kbg",
  kentuckybluegrass: "kbg",
  bluegrass: "kbg",
  rye: "rye",
  ryegrass: "rye",
  perennialryegrass: "rye",
  fine: "fine",
  finefescue: "fine",
  bermuda: "bermuda",
  bermudagrass: "bermuda",
  zoysia: "zoysia",
  zoysiagrass: "zoysia",
  centipede: "centipede",
  centipedegrass: "centipede",
  staug: "staug",
  staugustine: "staug",
  staugustinegrass: "staug",
  annualrye: "annualrye",
  annualryegrass: "annualrye",
  winterrye: "annualrye",
  italianryegrass: "annualrye",
  coolmix: "coolmix",
  coolseasonmix: "coolmix",
  coolseasonblend: "coolmix",
  mixedcoolseasonblend: "coolmix",
}

function normalizeGrass(raw: string): OverseedGrassId | undefined {
  const key = raw.toLowerCase().replace(/[-_.\s]+/g, "")
  return GRASS_PARAM[key]
}

function OverseedingContent({ picksSlot }: { picksSlot?: React.ReactNode }) {
  const searchParams = usePrerenderSafeSearchParams()
  const [initialValues, setInitialValues] = useState<OverseedingInitialValues | null>(null)
  const [liveResult, setLiveResult] = useState<OverseedingResult | null>(null)
  const trackedRef = useRef(false)
  // calculator_started on first input — result is gated on a known region (ZIP).
  const startedRef = useCalculatorStarted('overseeding')

  // URL prefill: ?zip & size & grass
  useEffect(() => {
    const zipParam = searchParams.get("zip")
    const sizeParam = searchParams.get("size")
    const grassParam = searchParams.get("grass")

    if (!zipParam && !sizeParam && !grassParam) return

    const size = sizeParam ? parseInt(sizeParam, 10) : undefined
    const next: OverseedingInitialValues = {
      zipCode: zipParam && /^\d{5}$/.test(zipParam) ? zipParam : undefined,
      lawnSize: size && !isNaN(size) ? size : undefined,
      grassType: grassParam ? normalizeGrass(grassParam) : undefined,
    }
    setInitialValues(next)
  }, [searchParams])

  // Fire calculator_used once, when a usable result (known region) first appears.
  useEffect(() => {
    if (!liveResult || trackedRef.current || !liveResult.regionKnown) return
    trackedRef.current = true
    analytics.calculatorUsed("overseeding", {
      inputs: {
        grass_type: liveResult.grassId,
        region: liveResult.region,
      },
      result: {
        mode: liveResult.mode,
        verdict: liveResult.verdict ?? undefined,
        lbs: liveResult.seed?.lbsRecommended ?? undefined,
      },
      source: "live_update",
    })
  }, [liveResult])

  return (
    <div ref={startedRef} className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <Reveal onMount y={6} className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-8 pb-8 md:pb-16">
        <BreadcrumbComponent
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Overseeding Calculator" },
          ]}
        />

        <div className="max-w-7xl mx-auto">
          <div className="relative mb-8 md:mb-12">
            <HeadlineHero
              eyebrow="Plan + quantity"
              title="How much seed to overseed, and"
              accent="exactly when"
              subtitle="Enter your ZIP, grass type, and lawn size. You'll get the pounds of seed to buy, the bags that add up to it, and your best overseeding window with a countdown."
            />
          </div>

          <div className="mb-8">
            <OverseedingForm initialValues={initialValues} onResultChange={setLiveResult} />
          </div>

          {liveResult && liveResult.regionKnown && (
            <div className="mb-8">
              <ResultEmailCaptureCard
                source="tool_result_overseeding"
                toolDisplayName="overseeding plan"
                subcopy="A reminder when your overseeding window opens, sent to your inbox."
                zipCode={initialValues?.zipCode}
                accountLead={{
                  redirectTo: buildToolUrl("overseeding-calculator", {
                    zip: initialValues?.zipCode,
                    lawnSize: initialValues?.lawnSize,
                  }),
                  headline: "Save my overseeding plan",
                  subcopy: "Keep this overseeding plan in a free account and track your lawn over time.",
                  onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_overseeding"),
                }}
                successSubcopy="Your lawn tips are on the way. Your overseeding plan stays right here to revisit anytime."
                onCaptured={() => {}}
              />
            </div>
          )}

          {/* Products never sit above a tool. Seed picks render below the form
              and its result, from the server shell so the anchors reach the
              served HTML. */}
          {picksSlot && <div className="mb-8">{picksSlot}</div>}

          <RelatedTools
            currentToolId="overseeding-calculator"
            prefillValues={{
              zip: initialValues?.zipCode,
              lawnSize: initialValues?.lawnSize,
            }}
          />
        </div>
      </Reveal>
    </div>
  )
}

export function OverseedingCalculatorPageContent({ picksSlot }: { picksSlot?: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <>
          <ToolPageLoadingFallback toolId="overseeding-calculator" />
          {/* The fallback IS the served HTML for this route (useSearchParams
              bails the island out of SSR), so the picks render here too. */}
          {picksSlot && (
            <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 pb-8">{picksSlot}</div>
          )}
        </>
      }
    >
      <SearchParamsHydrationGate dateSensitive>
        <OverseedingContent picksSlot={picksSlot} />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
